import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import OwnerDashboard from './OwnerDashboard'
import DailyClockIn from '../time/DailyClockIn'
import CrewSelection from './CrewSelection'
import { getTodayEastern } from '../../lib/dateHelpers'

var ARROW_R = String.fromCodePoint(0x2192)

export default function DashboardPage() {
  var { profile } = useAuth()
  var role = profile?.role

  if (role === 'owner') return <OwnerDashboard />

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.greeting}>
          WELCOME, {profile?.full_name?.split(' ')[0]?.toUpperCase() || 'TEAM'}
        </div>
        <div style={styles.role}>{role?.replace('_', ' ').toUpperCase()}</div>
      </div>

      {role === 'office_staff' && <OfficeDashboard />}
      {role === 'project_manager' && <PMDashboard />}
      {role === 'crew_leader' && <CrewDashboard profile={profile} />}
      {role === 'laborer' && <LaborerDashboard profile={profile} />}
    </div>
  )
}

function OfficeDashboard() {
  var navigate = useNavigate()
  var { activeBusiness } = useAuth()
  var [todayJobs, setTodayJobs] = useState([])
  var [pendingBids, setPendingBids] = useState(0)
  var [overdueInvs, setOverdueInvs] = useState(0)
  var [loading, setLoading] = useState(true)

  var fetchData = useCallback(async function() {
    setLoading(true)
    var today = getTodayEastern()
    var jobQ = supabase.from('jobs').select('*').eq('start_date', today).neq('status', 'cancelled')
    if (activeBusiness !== 'all') jobQ = jobQ.eq('business_id', activeBusiness)
    var { data: jobs } = await jobQ
    setTodayJobs(jobs || [])

    var { data: bids } = await supabase.from('bids').select('id').in('status', ['draft', 'submitted'])
    setPendingBids((bids || []).length)

    var { data: invs } = await supabase.from('invoices').select('id').in('status', ['sent', 'overdue']).lt('due_date', today)
    setOverdueInvs((invs || []).length)
    setLoading(false)
  }, [activeBusiness])

  useEffect(function() { fetchData() }, [fetchData])

  return (
    <div>
      <div style={styles.kpiRow}>
        <div style={styles.kpiCard} onClick={function() { navigate('/calendar') }}>
          <div style={styles.kpiValue}>{loading ? '-' : todayJobs.length}</div>
          <div style={styles.kpiLabel}>TODAY'S JOBS</div>
        </div>
        <div style={styles.kpiCard} onClick={function() { navigate('/bids') }}>
          <div style={styles.kpiValue}>{loading ? '-' : pendingBids}</div>
          <div style={styles.kpiLabel}>PENDING BIDS</div>
        </div>
        <div style={styles.kpiCard} onClick={function() { navigate('/invoicing') }}>
          <div style={{...styles.kpiValue, color: overdueInvs > 0 ? '#e05c5c' : '#4a8c6a'}}>{loading ? '-' : overdueInvs}</div>
          <div style={styles.kpiLabel}>OVERDUE INVOICES</div>
        </div>
      </div>
      {todayJobs.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>TODAY'S JOBS</div>
          {todayJobs.map(function(job) {
            return (
              <div key={job.id} style={styles.jobRow} onClick={function() { navigate('/calendar') }}>
                <div style={styles.jobTitle}>{job.title || job.client_name || 'Untitled'}</div>
                {job.site_address && <div style={styles.jobMeta}>{job.site_address}</div>}
              </div>
            )
          })}
        </div>
      )}
      <div style={styles.quickRow}>
        {[{ label: 'CALENDAR', path: '/calendar' }, { label: 'BIDS', path: '/bids' }, { label: 'INVOICING', path: '/invoicing' }, { label: 'TEAM', path: '/team' }].map(function(item) {
          return <button key={item.path} onClick={function() { navigate(item.path) }} style={styles.quickBtn}>{item.label} {ARROW_R}</button>
        })}
      </div>
    </div>
  )
}

function PMDashboard() {
  var navigate = useNavigate()
  var { activeBusiness } = useAuth()
  var [todayJobs, setTodayJobs] = useState([])
  var [pendingBids, setPendingBids] = useState(0)
  var [loading, setLoading] = useState(true)

  var fetchData = useCallback(async function() {
    setLoading(true)
    var today = getTodayEastern()
    var jobQ = supabase.from('jobs').select('*').eq('start_date', today).neq('status', 'cancelled')
    if (activeBusiness !== 'all') jobQ = jobQ.eq('business_id', activeBusiness)
    var { data: jobs } = await jobQ
    setTodayJobs(jobs || [])

    var { data: bids } = await supabase.from('bids').select('id').in('status', ['draft', 'submitted'])
    setPendingBids((bids || []).length)
    setLoading(false)
  }, [activeBusiness])

  useEffect(function() { fetchData() }, [fetchData])

  return (
    <div>
      <div style={styles.kpiRow}>
        <div style={styles.kpiCard} onClick={function() { navigate('/calendar') }}>
          <div style={styles.kpiValue}>{loading ? '-' : todayJobs.length}</div>
          <div style={styles.kpiLabel}>TODAY'S JOBS</div>
        </div>
        <div style={styles.kpiCard} onClick={function() { navigate('/bids') }}>
          <div style={styles.kpiValue}>{loading ? '-' : pendingBids}</div>
          <div style={styles.kpiLabel}>PENDING BIDS</div>
        </div>
      </div>
      {todayJobs.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>TODAY'S JOBS</div>
          {todayJobs.map(function(job) {
            return (
              <div key={job.id} style={styles.jobRow} onClick={function() { navigate('/calendar') }}>
                <div style={styles.jobTitle}>{job.title || job.client_name || 'Untitled'}</div>
                {job.site_address && <div style={styles.jobMeta}>{job.site_address}</div>}
              </div>
            )
          })}
        </div>
      )}
      <div style={styles.quickRow}>
        {[{ label: 'CALENDAR', path: '/calendar' }, { label: 'BIDS', path: '/bids' }, { label: 'TIME CLOCK', path: '/timeclock' }].map(function(item) {
          return <button key={item.path} onClick={function() { navigate(item.path) }} style={styles.quickBtn}>{item.label} {ARROW_R}</button>
        })}
      </div>
    </div>
  )
}

function CrewDashboard({ profile }) {
  var navigate = useNavigate()
  var [todayJobs, setTodayJobs] = useState([])
  var [loading, setLoading] = useState(true)

  var fetchData = useCallback(async function() {
    setLoading(true)
    var today = getTodayEastern()
    var { data: jobs } = await supabase.from('jobs').select('*').eq('start_date', today).neq('status', 'cancelled')
    setTodayJobs(jobs || [])
    setLoading(false)
  }, [])

  useEffect(function() { fetchData() }, [fetchData])

  return (
    <div>
      <DailyClockIn userId={profile?.id} />
      <CrewSelection crewLeaderId={profile?.id} />
      <div style={styles.section}>
        <div style={styles.sectionTitle}>TODAY'S JOBS</div>
        {loading ? <div style={styles.empty}>Loading...</div> : todayJobs.length === 0 ? (
          <div style={styles.empty}>No jobs scheduled for today.</div>
        ) : todayJobs.map(function(job) {
          return (
            <div key={job.id} style={{...styles.jobRow, borderLeft: '3px solid ' + (job.business_id === 'pools' ? '#b48c3c' : '#4a8c6a')}} onClick={function() { navigate('/calendar') }}>
              <div style={styles.jobTitle}>{job.title || job.client_name || 'Untitled'}</div>
              {job.site_address && <div style={styles.jobMeta}>{job.site_address}</div>}
              {job.contract_value && <div style={{...styles.jobMeta, color: '#b48c3c'}}>{'$' + Number(job.contract_value).toLocaleString()}</div>}
            </div>
          )
        })}
      </div>
      <div style={styles.quickRow}>
        {[{ label: 'CALENDAR', path: '/calendar' }, { label: 'MY DAY', path: '/myday' }, { label: 'SCHEDULE', path: '/schedule' }].map(function(item) {
          return <button key={item.path} onClick={function() { navigate(item.path) }} style={styles.quickBtn}>{item.label} {ARROW_R}</button>
        })}
      </div>
    </div>
  )
}

function LaborerDashboard({ profile }) {
  var [todayJobs, setTodayJobs] = useState([])
  var [bidLinesByJob, setBidLinesByJob] = useState({})
  var [bidNotesByJob, setBidNotesByJob] = useState({})
  var [loading, setLoading] = useState(true)
  var [etaJob, setEtaJob] = useState(null)

  var fetchData = useCallback(async function() {
    if (!profile?.id) return
    setLoading(true)
    var today = getTodayEastern()
    var { data: jobs } = await supabase.from('jobs').select('*').eq('start_date', today).neq('status', 'cancelled').contains('assigned_laborer_ids', [profile.id])
    setTodayJobs(jobs || [])
    var bidIds = (jobs || []).map(function(j) { return j.bid_id }).filter(Boolean)
    if (bidIds.length > 0) {
      var [linesRes, bidsRes] = await Promise.all([
        supabase.from('bid_lines').select('bid_id, description, quantity, unit, sort_order').in('bid_id', bidIds).order('sort_order'),
        supabase.from('bids').select('id, notes').in('id', bidIds),
      ])
      var byBid = {}
      ;(linesRes.data || []).forEach(function(l) { if (!byBid[l.bid_id]) byBid[l.bid_id] = []; byBid[l.bid_id].push(l) })
      var byJob = {}
      ;(jobs || []).forEach(function(j) { if (j.bid_id && byBid[j.bid_id]) byJob[j.id] = byBid[j.bid_id] })
      setBidLinesByJob(byJob)
      var notesByBid = {}
      ;(bidsRes.data || []).forEach(function(b) { if (b.notes) notesByBid[b.id] = b.notes })
      var notesByJob = {}
      ;(jobs || []).forEach(function(j) { if (j.bid_id && notesByBid[j.bid_id]) notesByJob[j.id] = notesByBid[j.bid_id] })
      setBidNotesByJob(notesByJob)
    } else {
      setBidLinesByJob({})
      setBidNotesByJob({})
    }
    setLoading(false)
  }, [profile?.id])

  useEffect(function() { fetchData() }, [fetchData])

  var todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })

  return (
    <div>
      <div style={styles.todayHeader}>{todayLabel}</div>
      <DailyClockIn userId={profile?.id} />
      <div style={styles.section}>
        <div style={styles.sectionTitle}>YOUR JOB{todayJobs.length !== 1 ? 'S' : ''} TODAY</div>
        {loading ? <div style={styles.empty}>Loading...</div> : todayJobs.length === 0 ? (
          <div style={styles.empty}>No jobs assigned to you today. Enjoy your day off!</div>
        ) : todayJobs.map(function(job) {
          var lines = bidLinesByJob[job.id] || []
          var bidNotes = bidNotesByJob[job.id]
          var mapsUrl = job.site_address ? 'https://maps.google.com/?q=' + encodeURIComponent(job.site_address) : null
          return (
            <div key={job.id} style={{...styles.jobCard, borderLeft: '3px solid ' + (job.business_id === 'pools' ? '#b48c3c' : '#4a8c6a')}}>
              <div style={styles.jobClient}>{job.client_name || job.title || 'Untitled'}</div>
              {job.title && job.client_name && <div style={styles.jobTitleSub}>{job.title}</div>}
              {job.site_address && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={styles.jobAddr}>
                  {String.fromCodePoint(0x1F4CD)} {job.site_address}
                </a>
              )}
              {job.client_phone && (
                <a href={'tel:' + job.client_phone} style={styles.jobAddr}>
                  {String.fromCodePoint(0x1F4DE)} {job.client_phone}
                </a>
              )}
              {lines.length > 0 && (
                <div style={styles.lineItems}>
                  <div style={styles.lineItemsLabel}>WORK SCOPE</div>
                  {lines.map(function(l, i) {
                    return (
                      <div key={i} style={styles.lineItem}>
                        <span style={styles.bullet}>{String.fromCodePoint(0x2022)}</span>
                        <span>{l.description}{l.quantity ? ' (' + l.quantity + (l.unit ? ' ' + l.unit : '') + ')' : ''}</span>
                      </div>
                    )
                  })}
                </div>
              )}
              {(bidNotes || job.notes) && (
                <div style={styles.lineItems}>
                  <div style={styles.lineItemsLabel}>NOTES</div>
                  {bidNotes && <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{bidNotes}</div>}
                  {job.notes && <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginTop: bidNotes ? '6px' : 0 }}>{job.notes}</div>}
                </div>
              )}
              <div style={styles.jobActions}>
                {job.site_address && (
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={styles.actionBtn}>
                    {String.fromCodePoint(0x1F5FA)} DIRECTIONS
                  </a>
                )}
                {(job.client_phone || job.client_email) && (
                  <button onClick={function() { setEtaJob(job) }} style={styles.etaBtn}>
                    SEND ETA
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {etaJob && <LaborerETAModal job={etaJob} profile={profile} onClose={function() { setEtaJob(null) }} />}
    </div>
  )
}

function LaborerETAModal({ job, profile, onClose }) {
  var [data, setData] = useState({ drive: null, driveText: '', etaStr: '', message: '', loading: true, sending: false, from: '' })

  useEffect(function() {
    (async function() {
      var fromAddr = '42.6320,-83.7563'
      try {
        var gps = await new Promise(function(resolve) {
          if (!navigator.geolocation) return resolve(null)
          navigator.geolocation.getCurrentPosition(
            function(p) { resolve({ lat: p.coords.latitude, lng: p.coords.longitude }) },
            function() { resolve(null) },
            { enableHighAccuracy: true, timeout: 8000 }
          )
        })
        if (gps) fromAddr = gps.lat + ',' + gps.lng
      } catch (e) { /* ignore */ } // eslint-disable-line no-unused-vars
      var driveMin = null, driveText = null
      try {
        var resp = await fetch('/api/drive-times', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ origin: fromAddr, destination: job.site_address }) })
        var j = await resp.json()
        if (j.success && j.duration_minutes) { driveMin = j.duration_minutes; driveText = j.duration_text || (driveMin + ' mins') }
      } catch (e) { /* ignore */ } // eslint-disable-line no-unused-vars
      var eta = driveMin ? new Date(Date.now() + driveMin * 60000) : null
      var etaStr = eta ? eta.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'shortly'
      var firstName = (job.client_name || 'there').split(' ')[0]
      var bizName = job.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Clearing Co.'
      var msg = 'Hi ' + firstName + ', this is ' + bizName + '. We\'re on our way and expect to arrive around ' + etaStr + '. We\'ll give you a call when we\'re close. Thanks!'
      setData({ drive: driveMin, driveText: driveText, etaStr: etaStr, message: msg, loading: false, sending: false, from: fromAddr })
    })()
  }, [job])

  var send = async function(via) {
    setData(function(d) { return Object.assign({}, d, { sending: true }) })
    try {
      if (via === 'sms' && job.client_phone) {
        await fetch('/api/send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: job.client_phone, message: data.message, business_id: job.business_id }) })
      }
      if (via === 'email' && job.client_email) {
        await fetch('/api/send-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: job.client_email, subject: 'On our way!', body: data.message, business_id: job.business_id }) })
      }
      var etaLogRes = await supabase.from('eta_messages').insert({
        job_id: job.id, sent_by: profile?.id, client_name: job.client_name, client_phone: job.client_phone,
        client_email: job.client_email, sent_via: via, message: data.message, drive_minutes: data.drive,
        eta_time: data.etaStr, from_address: data.from, to_address: job.site_address
      })
      if (etaLogRes.error) console.warn('eta_messages log failed', etaLogRes.error)
      alert('ETA sent!')
      onClose()
    } catch (e) { alert('Failed: ' + e.message) }
    setData(function(d) { return Object.assign({}, d, { sending: false }) })
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={function(e) { e.stopPropagation() }}>
        <div style={styles.modalTitle}>SEND ETA</div>
        <div style={styles.modalClient}>{job.client_name}</div>
        {data.loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>Calculating drive time...</div>
        ) : (
          <div>
            {data.drive != null && (
              <div style={styles.etaBox}>
                <div style={styles.etaDrive}>Drive time: {data.driveText || data.drive + ' min'}</div>
                <div style={styles.etaTime}>Arrival: {data.etaStr}</div>
              </div>
            )}
            <div style={styles.etaMsgLabel}>MESSAGE</div>
            <textarea
              value={data.message}
              onChange={function(e) { setData(Object.assign({}, data, { message: e.target.value })) }}
              style={styles.etaMsgBox}
            />
            <div style={styles.modalActions}>
              <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
              {job.client_phone && <button disabled={data.sending} onClick={function() { send('sms') }} style={styles.sendBtn}>{data.sending ? '...' : 'SEND SMS'}</button>}
              {job.client_email && <button disabled={data.sending} onClick={function() { send('email') }} style={styles.sendEmailBtn}>{data.sending ? '...' : 'SEND EMAIL'}</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

var styles = {
  container: { maxWidth: '900px', fontFamily: "'Barlow', sans-serif" },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '36px' },
  greeting: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  role: { backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '6px 12px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#b48c3c' },

  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' },
  kpiCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px', textAlign: 'center', cursor: 'pointer' },
  kpiValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '36px', fontWeight: 700, color: '#ffffff', lineHeight: 1 },
  kpiLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginTop: '8px' },

  section: { marginBottom: '24px' },
  sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)' },

  empty: { padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '14px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' },

  jobRow: { padding: '14px 16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', marginBottom: '4px', cursor: 'pointer' },
  jobTitle: { fontSize: '15px', fontWeight: 600, color: '#ffffff' },
  jobMeta: { fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginTop: '3px' },

  quickRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '24px' },
  quickBtn: { padding: '12px 20px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer' },

  todayHeader: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px', marginTop: '-20px' },
  jobCard: { padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', marginBottom: '10px' },
  jobClient: { fontSize: '17px', fontWeight: 700, color: '#ffffff', marginBottom: '2px' },
  jobTitleSub: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' },
  jobAddr: { display: 'block', fontSize: '14px', color: '#b48c3c', textDecoration: 'none', marginBottom: '10px', padding: '6px 0' },
  lineItems: { marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' },
  lineItemsLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  lineItem: { display: 'flex', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', padding: '3px 0', lineHeight: '1.4' },
  bullet: { color: '#b48c3c', flexShrink: 0 },
  jobActions: { display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' },
  actionBtn: { flex: 1, minWidth: '120px', padding: '10px 12px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '4px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', textAlign: 'center', textDecoration: 'none', cursor: 'pointer' },
  etaBtn: { flex: 1, minWidth: '120px', padding: '10px 12px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '4px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },

  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { backgroundColor: '#0d1420', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '8px', padding: '20px', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c', marginBottom: '4px' },
  modalClient: { fontSize: '16px', fontWeight: 600, color: '#ffffff', marginBottom: '16px' },
  etaBox: { padding: '12px', backgroundColor: 'rgba(180,140,60,0.08)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', marginBottom: '14px' },
  etaDrive: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' },
  etaTime: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, color: '#b48c3c', letterSpacing: '1px' },
  etaMsgLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' },
  etaMsgBox: { width: '100%', minHeight: '90px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow', sans-serif", fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' },
  cancelBtn: { padding: '10px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  sendBtn: { flex: 1, padding: '10px 14px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  sendEmailBtn: { flex: 1, padding: '10px 14px', backgroundColor: '#4a6a8c', border: 'none', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
}
